import { useState, useRef, useEffect, useMemo } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/common/PageHeader";
import { ConnectorCard } from "@/components/connectors/ConnectorCard";
import { DsaPartner, DsaPartnerType } from "@/types";
import { useDsaPartners } from "@/hooks/useDsaPartners";
import { useLeads } from "@/hooks/useLeads";
import { useTasks } from "@/hooks/useTasks";
import { useAppConfig } from "@/hooks/useAppConfig";
import { useFileUpload } from "@/hooks/useFileUpload";
import { canManagePayouts } from "@/lib/rbac";
import { useAuth } from "@/contexts/AuthContext";
import {
  INDIAN_STATES,
  INDIAN_CITIES,
  TEST_ADMIN_USER_ID,
} from "@/lib/constants";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  getCurrentLocation,
  calculateDistance,
} from "@/services/geolocationService";
import { FileUploadSection } from "@/components/common/FileUploadSection";
import { getAllActiveUsers } from "@/services/userService";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  Users,
  MapPin,
  UserPlus,
  Share2,
  ChevronDown,
  User,
  Phone,
  Mail,
  Building,
  FileText,
  Filter,
  Calendar,
  Map,
  RotateCcw,
} from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { MapLocationPicker } from "@/components/common/MapLocationPicker";
import { useModal } from "@/contexts/ModalContext";

import { differenceInDays } from "date-fns";
import { searchItems } from "@/lib/searchUtils";

type FilterType = "all" | "favorites" | "deactive" | DsaPartnerType;
type PendingFilter =
  | "none"
  | "visit_7"
  | "visit_15"
  | "visit_30"
  | "followup_7"
  | "followup_15"
  | "followup_30";

const connectorFilters: { value: FilterType; label: string }[] = [
  { value: "all", label: "All" },
  { value: "favorites", label: "★ Favorites" },
  { value: "dsa", label: "DSA" },
  { value: "hlc", label: "HLC" },
  { value: "financial_advisor", label: "Financial Advisor" },
  { value: "banker", label: "Banker" },
  { value: "others", label: "Others" },
  { value: "deactive", label: "Deactive" },
];

export default function DsaPartnersPage() {
  const navigate = useNavigate();
  const {
    partners: connectors,
    loading,
    toggleFavorite,
    updatePartner,
    addPartner,
    uploadDocument,
  } = useDsaPartners();
  const { leads } = useLeads();
  const { tasks } = useTasks();
  const { config } = useAppConfig();
  const { user } = useAuth();
  const canManage = canManagePayouts(user);

  const terminalStatuses = [
    "closed",
    "removed",
    "rejected",
    "disbursed",
    "payout_released",
  ];
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingFilter, setPendingFilter] = useState<PendingFilter>("none");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareWithAll, setShareWithAll] = useState(false);
  const [selectedColleagues, setSelectedColleagues] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<
    Array<{ id?: string; userId?: string; name: string }>
  >([]);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    open: boolean;
    type: "visit" | "followup";
    partnerId: string;
    partnerName: string;
  }>({ open: false, type: "visit", partnerId: "", partnerName: "" });
  const { registerModal, unregisterModal } = useModal();

  const fileUploadManager = useFileUpload({
    maxSizeMB: config.maxFileUploadSizeMB,
    maxFiles: config.maxFileUploads,
    allowedFileTypes: config.allowedFileTypes,
    uploadMode: "deferred",
  });
  const fileManagerFiles = fileUploadManager.pendingFiles;
  const fileManagerReset = fileUploadManager.resetPending;

  const [newPartner, setNewPartner] = useState({
    name: "",
    type: "dsa" as DsaPartnerType,
    businessName: "",
    whatsappNumber: "",
    address: "",
    pincode: "",
    city: "",
    state: "",
    email: "",
    remark: "",
    location: null as { lat: number; lng: number } | null,
  });

  const cityOptions = INDIAN_CITIES.map((city) => ({
    value: city,
    label: city,
  }));

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await getAllActiveUsers();
        setAvailableUsers(
          usersData.filter((u) => (u.userId || u.id) !== TEST_ADMIN_USER_ID),
        );
      } catch (error) {
        console.error("Error loading users:", error);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    if (!addDialogOpen) return;
    registerModal("dsa-add-partner-sheet", () => setAddDialogOpen(false), 1);
    return () => unregisterModal("dsa-add-partner-sheet");
  }, [addDialogOpen, registerModal, unregisterModal]);

  useEffect(() => {
    if (!mapPickerOpen) return;
    registerModal("dsa-map-picker", () => setMapPickerOpen(false), 2);
    return () => unregisterModal("dsa-map-picker");
  }, [mapPickerOpen, registerModal, unregisterModal]);

  const handleToggleShareMember = (memberId: string) => {
    setSelectedColleagues((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId],
    );
    setShareWithAll(false);
  };

  const handleShareWithAll = () => {
    if (shareWithAll) {
      setSelectedColleagues([]);
      setShareWithAll(false);
    } else {
      setSelectedColleagues(availableUsers.map((m) => m.userId || m.id!));
      setShareWithAll(true);
    }
  };

  const handleToggleFavorite = async (id: string) => {
    const connector = connectors.find((c) => c.id === id);
    if (!connector) return;

    try {
      await toggleFavorite(id, !connector.isFavorite);
      toast.success(
        connector.isFavorite
          ? `Removed ${connector.name} from favorites`
          : `Added ${connector.name} to favorites`,
      );
    } catch (error) {
      toast.error("Failed to update favorite status");
    }
  };

  const handleMarkVisit = async (id: string) => {
    const connector = connectors.find((c) => c.id === id);
    if (!connector) return;

    if (!connector.location) {
      toast.error(
        "Partner location not set. Please update partner's geo-location first.",
      );
      return;
    }

    try {
      const position = await getCurrentLocation(true, 10000);
      const currentLat = position.lat;
      const currentLng = position.lng;
      const partnerLat = connector.location.lat;
      const partnerLng = connector.location.lng;
      const distance = calculateDistance(
        currentLat,
        currentLng,
        partnerLat,
        partnerLng,
      );

      if (distance > 200) {
        toast.error(
          `You are ${Math.round(distance)} meters away from ${connector.name}'s location. Please be within 200 meters to mark visit.`,
        );
        return;
      }

      setConfirmAction({
        open: true,
        type: "visit",
        partnerId: id,
        partnerName: connector.name,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Could not verify location. Please enable location services and try again.";
      toast.error(errorMessage);
    }
  };

  const handleConfirmAction = async () => {
    const { type, partnerId, partnerName } = confirmAction;
    const connector = connectors.find((c) => c.id === partnerId);
    if (!connector) return;

    try {
      if (type === "visit") {
        await updatePartner(partnerId, {
          lastVisitDate: new Date(),
          lastFollowUpDate: new Date(),
          totalVisits: connector.totalVisits + 1,
        });
        toast.success(`Visit and follow-up marked for ${partnerName}`);
      } else {
        await updatePartner(partnerId, { lastFollowUpDate: new Date() });
        toast.success(`Follow-up marked for ${partnerName}`);
      }
    } catch (error) {
      toast.error(`Failed to mark ${type}`);
    }

    setConfirmAction((prev) => ({ ...prev, open: false }));
  };

  const handleMarkFollowUpOnly = async (id: string) => {
    const connector = connectors.find((c) => c.id === id);
    if (!connector) return;

    setConfirmAction({
      open: true,
      type: "followup",
      partnerId: id,
      partnerName: connector.name,
    });
  };

  const handleGetCurrentLocation = async () => {
    setIsGettingLocation(true);
    try {
      const position = await getCurrentLocation(true, 10000);
      setNewPartner((p) => ({
        ...p,
        location: { lat: position.lat, lng: position.lng },
      }));
      toast.success("Location updated successfully");
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Could not get location. Please enable location services.";
      toast.error(errorMessage);
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleCardClick = (id: string) => {
    navigate(`/tools/dsa-partners/${id}`);
  };

  const validatePartnerForm = () => {
    if (!newPartner.name?.trim()) {
      toast.error("Partner name is required");
      return false;
    }

    if (!newPartner.whatsappNumber?.trim()) {
      toast.error("WhatsApp number is required");
      return false;
    }

    if (!/^[6-9]\d{9}$/.test(newPartner.whatsappNumber)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return false;
    }

    if (
      newPartner.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newPartner.email)
    ) {
      toast.error("Please enter a valid email address");
      return false;
    }

    if (newPartner.pincode && !/^\d{6}$/.test(newPartner.pincode)) {
      toast.error("Please enter a valid 6-digit pincode");
      return false;
    }

    return true;
  };

  const handleAddPartner = async () => {
    if (!validatePartnerForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const newConnector: Omit<DsaPartner, "id"> = {
        name: newPartner.name,
        type: newPartner.type,
        ...(newPartner.businessName.trim() && {
          businessName: newPartner.businessName.trim(),
        }),
        whatsappNumber: `+91${newPartner.whatsappNumber}`,
        address: newPartner.address,
        pincode: newPartner.pincode,
        city: newPartner.city,
        state: newPartner.state,
        email: newPartner.email,
        remark: newPartner.remark,
        ...(newPartner.location && { location: newPartner.location }),
        isFavorite: false,
        totalVisits: 0,
        createdAt: new Date(),
        documents: [],
        sharedWith: Array.from(
          new Set([TEST_ADMIN_USER_ID, ...selectedColleagues]),
        ),
      };

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error("Request timed out after 30 seconds")),
          30000,
        ),
      );
      const createdPartner = await Promise.race([
        addPartner(newConnector),
        timeoutPromise,
      ]);

      if (fileManagerFiles.length > 0) {
        const uploadedDocuments: Awaited<ReturnType<typeof uploadDocument>>[] =
          [];
        const results = await Promise.allSettled(
          fileManagerFiles.map((file) =>
            uploadDocument(file, createdPartner.id),
          ),
        );
        const failedNames: string[] = [];
        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            uploadedDocuments.push(result.value);
          } else {
            console.error("Error uploading document:", result.reason);
            failedNames.push(fileManagerFiles[index].name);
          }
        });
        if (uploadedDocuments.length > 0) {
          await updatePartner(createdPartner.id, {
            documents: uploadedDocuments,
          });
        }
        if (failedNames.length > 0) {
          toast.warning(
            `Partner saved, but failed to upload: ${failedNames.join(", ")}`,
          );
        }
      }

      toast.success(`Added ${newPartner.name} successfully`);
      setAddDialogOpen(false);
      setNewPartner({
        name: "",
        type: "dsa",
        businessName: "",
        whatsappNumber: "",
        address: "",
        pincode: "",
        city: "",
        state: "",
        email: "",
        remark: "",
        location: null,
      });
      fileManagerReset();
      setSelectedColleagues([]);
      setShareWithAll(false);
      setShareOpen(false);
    } catch (error) {
      console.error("Error adding DSA partner:", error);
      const isDuplicate =
        error instanceof Error && error.message.startsWith("DUPLICATE_DSA:");
      const isTimeout =
        error instanceof Error && error.message.includes("timed out");
      toast.error(
        isDuplicate
          ? "You already have a DSA partner with this WhatsApp number."
          : isTimeout
            ? "Request timed out. Please check your connection and try again."
            : "Failed to add partner. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredConnectors = useMemo(() => {
    const searchResults = searchItems(searchQuery, connectors, [
      "name",
      "city",
      "pincode",
      "whatsappNumber",
      "state",
      "address",
      "email",
      "remark",
      "designation",
      "type",
    ]);

    return searchResults.filter((connector) => {
      const isDeactivated = connector.isActive === false;

      let matchesType = true;
      if (activeFilter === "deactive") {
        matchesType = isDeactivated;
      } else if (activeFilter === "favorites") {
        matchesType = connector.isFavorite && !isDeactivated;
      } else if (activeFilter === "all") {
        matchesType = !isDeactivated;
      } else {
        matchesType = connector.type === activeFilter && !isDeactivated;
      }

      let matchesPending = true;
      if (pendingFilter !== "none") {
        const now = new Date();
        const [type, daysStr] = pendingFilter.split("_");
        const days = parseInt(daysStr);

        if (type === "visit") {
          matchesPending =
            !connector.lastVisitDate ||
            differenceInDays(now, connector.lastVisitDate) > days;
        } else if (type === "followup") {
          matchesPending =
            !connector.lastFollowUpDate ||
            differenceInDays(now, connector.lastFollowUpDate) > days;
        }
      }

      return matchesType && matchesPending;
    });
  }, [searchQuery, connectors, activeFilter, pendingFilter]);

  const sortedConnectors = [...filteredConnectors].sort((a, b) => {
    if (a.isFavorite && !b.isFavorite) return -1;
    if (!a.isFavorite && b.isFavorite) return 1;
    const aDate = a.lastVisitDate?.getTime() || 0;
    const bDate = b.lastVisitDate?.getTime() || 0;
    return bDate - aDate;
  });

  const getCounts = () => {
    const counts: Record<string, number> = {
      all: 0,
      favorites: 0,
      deactive: 0,
    };
    connectors.forEach((c) => {
      const isDeactivated = c.isActive === false;
      if (isDeactivated) {
        counts.deactive++;
        return;
      }
      counts.all++;
      if (c.isFavorite) counts.favorites++;
      counts[c.type] = (counts[c.type] || 0) + 1;
    });
    return counts;
  };

  const counts = getCounts();

  return (
    <AppLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex-1 min-w-0">
            <PageHeader
              title="DSA Partners"
              subtitle="Manage your DSA partner network"
            />
          </div>
          {canManage && (
            <Button
              onClick={() => setAddDialogOpen(true)}
              className="shrink-0 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-md hover:shadow-lg transition-all"
              size="sm"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              <span>Add Partner</span>
            </Button>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, city, phone, state, designation, type, email, address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={pendingFilter !== "none" ? "default" : "outline"}
                  size="icon"
                  className={cn(
                    "h-10 w-10 shrink-0",
                    pendingFilter !== "none" &&
                      "bg-green-600 hover:bg-green-700",
                  )}
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>Visit Pending</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() =>
                    setPendingFilter(
                      pendingFilter === "visit_7" ? "none" : "visit_7",
                    )
                  }
                  className={cn(
                    pendingFilter === "visit_7" && "bg-green-50 text-green-700",
                  )}
                >
                  <Calendar className="h-4 w-4 mr-2" /> Last 7 days
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    setPendingFilter(
                      pendingFilter === "visit_15" ? "none" : "visit_15",
                    )
                  }
                  className={cn(
                    pendingFilter === "visit_15" &&
                      "bg-green-50 text-green-700",
                  )}
                >
                  <Calendar className="h-4 w-4 mr-2" /> Last 15 days
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    setPendingFilter(
                      pendingFilter === "visit_30" ? "none" : "visit_30",
                    )
                  }
                  className={cn(
                    pendingFilter === "visit_30" &&
                      "bg-green-50 text-green-700",
                  )}
                >
                  <Calendar className="h-4 w-4 mr-2" /> Last 30 days
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>FollowUp Pending</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() =>
                    setPendingFilter(
                      pendingFilter === "followup_7" ? "none" : "followup_7",
                    )
                  }
                  className={cn(
                    pendingFilter === "followup_7" &&
                      "bg-green-50 text-green-700",
                  )}
                >
                  <Calendar className="h-4 w-4 mr-2" /> Last 7 days
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    setPendingFilter(
                      pendingFilter === "followup_15" ? "none" : "followup_15",
                    )
                  }
                  className={cn(
                    pendingFilter === "followup_15" &&
                      "bg-green-50 text-green-700",
                  )}
                >
                  <Calendar className="h-4 w-4 mr-2" /> Last 15 days
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    setPendingFilter(
                      pendingFilter === "followup_30" ? "none" : "followup_30",
                    )
                  }
                  className={cn(
                    pendingFilter === "followup_30" &&
                      "bg-green-50 text-green-700",
                  )}
                >
                  <Calendar className="h-4 w-4 mr-2" /> Last 30 days
                </DropdownMenuItem>
                {pendingFilter !== "none" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setPendingFilter("none")}
                      className="text-destructive"
                    >
                      Clear Filter
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {pendingFilter !== "none" && (
            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className="bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer"
                onClick={() => setPendingFilter("none")}
              >
                {pendingFilter.startsWith("visit") ? "📍 Visit" : "📞 FollowUp"}{" "}
                Pending &gt; {pendingFilter.split("_")[1]}d
                <span className="ml-1">✕</span>
              </Badge>
              <span className="text-xs text-muted-foreground">
                {filteredConnectors.length} results
              </span>
            </div>
          )}

          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
            {connectorFilters.map((filter) => {
              const count = counts[filter.value] || 0;
              return (
                <button
                  key={filter.value}
                  onClick={() => setActiveFilter(filter.value)}
                  className={cn(
                    "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all whitespace-nowrap",
                    activeFilter === filter.value
                      ? filter.value === "favorites"
                        ? "bg-gold text-gold-foreground shadow-md"
                        : filter.value === "deactive"
                          ? "bg-red-500 text-white shadow-md"
                          : "bg-primary text-primary-foreground shadow-md"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                  )}
                >
                  {filter.label}
                  <Badge
                    variant="secondary"
                    className="bg-background/20 text-current"
                  >
                    {count}
                  </Badge>
                </button>
              );
            })}
          </div>

          <div className="space-y-3">
            {sortedConnectors.map((connector, index) => (
              <motion.div
                key={connector.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <ConnectorCard
                  connector={connector}
                  onToggleFavorite={handleToggleFavorite}
                  onMarkVisit={handleMarkVisit}
                  onMarkFollowUp={handleMarkFollowUpOnly}
                  detailRouteBase="/tools/dsa-partners"
                  creatorName={
                    connector.createdBy
                      ? availableUsers.find(
                          (u) => (u.userId || u.id) === connector.createdBy,
                        )?.name
                      : undefined
                  }
                  leadsCount={
                    leads.filter(
                      (l) =>
                        l.connectorId === connector.id &&
                        !terminalStatuses.includes(l.status),
                    ).length
                  }
                  pendingRemindersCount={
                    tasks.filter(
                      (t) => t.connectorId === connector.id && !t.completed,
                    ).length
                  }
                />
              </motion.div>
            ))}

            {sortedConnectors.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No DSA partners found</p>
              </motion.div>
            )}
          </div>
        </div>

        <Sheet open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <SheetContent
            side="bottom"
            className="h-[90vh] rounded-t-3xl flex flex-col"
          >
            <SheetHeader className="pb-4 px-4 flex-shrink-0">
              <div className="flex items-center justify-center gap-2">
                <SheetTitle className="text-xl font-bold">
                  Add DSA Partner
                </SheetTitle>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-lg shrink-0"
                  title="Clear form"
                  onClick={() => {
                    setNewPartner({
                      name: "",
                      type: "dsa",
                      businessName: "",
                      whatsappNumber: "",
                      address: "",
                      pincode: "",
                      city: "",
                      state: "",
                      email: "",
                      remark: "",
                      location: null,
                    });
                    fileManagerReset();
                    setSelectedColleagues([]);
                    setShareWithAll(false);
                    setShareOpen(false);
                    toast.info("Form cleared");
                  }}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6">
              <div className="space-y-5 pb-8">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <User className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">
                      Basic Information
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Name *</Label>
                    <Input
                      value={newPartner.name}
                      onChange={(e) =>
                        setNewPartner((p) => ({ ...p, name: e.target.value }))
                      }
                      placeholder="Ex - Rajesh Kumar"
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Type</Label>
                    <Select
                      value={newPartner.type}
                      onValueChange={(v) =>
                        setNewPartner((p) => ({
                          ...p,
                          type: v as DsaPartnerType,
                        }))
                      }
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dsa">DSA</SelectItem>
                        <SelectItem value="hlc">HLC</SelectItem>
                        <SelectItem value="financial_advisor">
                          Financial Advisor
                        </SelectItem>
                        <SelectItem value="banker">Banker</SelectItem>
                        <SelectItem value="others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Business Name</Label>
                    <Input
                      value={newPartner.businessName}
                      onChange={(e) =>
                        setNewPartner((p) => ({
                          ...p,
                          businessName: e.target.value,
                        }))
                      }
                      placeholder="Enter Your Business Name"
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <Phone className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">
                      Contact Details
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      WhatsApp Number *
                    </Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">
                        +91
                      </span>
                      <Input
                        type="tel"
                        value={newPartner.whatsappNumber}
                        onChange={(e) =>
                          setNewPartner((p) => ({
                            ...p,
                            whatsappNumber: e.target.value.replace(/\D/g, ""),
                          }))
                        }
                        maxLength={10}
                        placeholder="Ex - 9876543210"
                        className="h-12 rounded-l-none"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Email</Label>
                    <Input
                      type="email"
                      value={newPartner.email}
                      onChange={(e) =>
                        setNewPartner((p) => ({ ...p, email: e.target.value }))
                      }
                      placeholder="Ex - rajesh@email.com"
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <Building className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">
                      Address Details
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Address</Label>
                    <Input
                      value={newPartner.address}
                      onChange={(e) =>
                        setNewPartner((p) => ({
                          ...p,
                          address: e.target.value,
                        }))
                      }
                      placeholder="Ex - 123, ABC Road, Near XYZ Mall"
                      className="h-12"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">City</Label>
                      <SearchableSelect
                        options={cityOptions}
                        value={newPartner.city}
                        onValueChange={(val) =>
                          setNewPartner((p) => ({ ...p, city: val }))
                        }
                        placeholder="Ex - Mumbai"
                        searchPlaceholder="Search cities..."
                        emptyText="No city found."
                        className="h-12"
                        fullWidthPopover
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Pincode</Label>
                      <Input
                        type="tel"
                        value={newPartner.pincode}
                        onChange={(e) =>
                          setNewPartner((p) => ({
                            ...p,
                            pincode: e.target.value.replace(/\D/g, ""),
                          }))
                        }
                        maxLength={6}
                        placeholder="Ex - 400001"
                        className="h-12"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">State *</Label>
                    <Select
                      value={newPartner.state}
                      onValueChange={(value) =>
                        setNewPartner((p) => ({ ...p, state: value }))
                      }
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDIAN_STATES.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <FileText className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">Notes</h3>
                  </div>
                  <div className="space-y-2">
                    <Textarea
                      value={newPartner.remark}
                      onChange={(e) =>
                        setNewPartner((p) => ({ ...p, remark: e.target.value }))
                      }
                      placeholder="Ex - Deals in residential properties, Andheri area specialist"
                      rows={3}
                      className="resize-none"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <MapPin className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">Location</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 h-12"
                        onClick={() => setMapPickerOpen(true)}
                      >
                        <Map className="h-4 w-4 mr-2" />
                        {newPartner.location
                          ? "Location Set ✓ — Change"
                          : "Pick Location on Map"}
                      </Button>
                    </div>
                    {newPartner.location && (
                      <p className="text-xs text-muted-foreground">
                        📍 {newPartner.location.lat.toFixed(6)},{" "}
                        {newPartner.location.lng.toFixed(6)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <FileText className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">Documents</h3>
                  </div>
                  <FileUploadSection
                    files={fileManagerFiles}
                    onCameraCapture={fileUploadManager.handleCameraCapture}
                    onFileUpload={fileUploadManager.handleFileUpload}
                    onRemoveFile={fileUploadManager.handleRemovePendingFile}
                    onImageClick={fileUploadManager.openImageViewer}
                    imageViewerState={{
                      isOpen: fileUploadManager.imageViewerState.isOpen,
                      images: fileUploadManager.imageViewerState.images,
                      currentIndex:
                        fileUploadManager.imageViewerState.currentIndex,
                      setCurrentIndex:
                        fileUploadManager.imageViewerState.setCurrentIndex,
                      onClose: fileUploadManager.closeImageViewer,
                    }}
                    label={`Documents / Visiting Card (Max ${config.maxFileUploadSizeMB}MB per file)`}
                    helpText="Photos, documents, PDFs, etc."
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <Share2 className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">
                      Share with Colleagues
                    </h3>
                  </div>
                  <Collapsible open={shareOpen} onOpenChange={setShareOpen}>
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between"
                        type="button"
                      >
                        <span className="flex items-center gap-2">
                          <Share2 className="h-4 w-4" />
                          <span>
                            {selectedColleagues.length > 0
                              ? `Sharing with ${shareWithAll ? "all" : selectedColleagues.length} colleague${selectedColleagues.length > 1 ? "s" : ""}`
                              : "Share with colleagues"}
                          </span>
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${shareOpen ? "rotate-180" : ""}`}
                        />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-3 space-y-2">
                      <div
                        className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer hover:bg-secondary/50"
                        onClick={handleShareWithAll}
                      >
                        <Checkbox
                          checked={shareWithAll}
                          onCheckedChange={handleShareWithAll}
                        />
                        <span className="text-sm font-medium">
                          Share with all
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {availableUsers.map((member) => {
                          const userId = member.userId || member.id!;
                          return (
                            <div
                              key={userId}
                              className="flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer hover:bg-secondary/50"
                              onClick={() => handleToggleShareMember(userId)}
                            >
                              <Checkbox
                                checked={selectedColleagues.includes(userId)}
                                onCheckedChange={() =>
                                  handleToggleShareMember(userId)
                                }
                              />
                              <span className="text-sm truncate">
                                {member.name.split(" ")[0]}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>

                <Button
                  onClick={handleAddPartner}
                  className="w-full h-12 mt-4 bg-gradient-to-r from-[#2d5a6b] to-[#4ea39f] hover:from-[#234851] hover:to-[#3d8985] text-white font-semibold"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Adding Partner..." : "Add Partner"}
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <MapLocationPicker
        open={mapPickerOpen}
        onOpenChange={setMapPickerOpen}
        initialLocation={newPartner.location}
        onLocationSelect={(location) => {
          setNewPartner((p) => ({ ...p, location }));
          toast.success("Location selected from map");
        }}
      />

      <AlertDialog
        open={confirmAction.open}
        onOpenChange={(open) => setConfirmAction((prev) => ({ ...prev, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction.type === "visit"
                ? "Confirm Visit"
                : "Confirm Follow-up"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction.type === "visit"
                ? `Mark visit as done for ${confirmAction.partnerName}? This will also update the follow-up date.`
                : `Mark follow-up as done for ${confirmAction.partnerName}?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmAction}>
              Yes, Mark Done
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
